import React from 'react';
import PropTypes from 'prop-types';
import findIndex from 'lodash/findIndex';
import isObject from 'lodash/isObject';
import pick from 'lodash/pick';
import styles from './WorkbenchBuilder.css';

import Loading from 'zignal-landscape/lib/Loading';

import WorkbenchFieldForm from './WorkbenchFieldForm.js';
import WorkbenchFieldList from './WorkbenchFieldList.js';
import { checkStatus, parseJSON } from '../utils/http-helper.js';


/**
 * Renders as
 * - Account Settings > Custom Attributes for an Account with Workbench enabled
 * - Workspace Settings > Custom Attributes for a Workspace with Workbench enabled
 *
 * @see Views.AdminTabNav#customWorkbenchFieldBuilder
 * @see Views.AccountCustomAttributesPageView#initialize
 */
class WorkbenchBuilder extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      fields: this.props.fields,
      newFieldName: null,
      newFieldType: null,
      newFieldHasMultipleValues: null,
      isLoadingFields: true
    };

    this.handleFieldAdd = this.handleFieldAdd.bind(this);
    this.handleFieldUpdate = this.handleFieldUpdate.bind(this);
    this.handleFieldDelete = this.handleFieldDelete.bind(this);
    this.handleFieldValueUpdate = this.handleFieldValueUpdate.bind(this);
    this.handleFieldValueAdd = this.handleFieldValueAdd.bind(this);
    this.handleFieldValueDelete = this.handleFieldValueDelete.bind(this);
  }

  componentDidMount() {
    const ERROR_TITLE = 'Server failed to get WorkWise fields';

    return fetch(this.getWorkbenchEndpoint(), {
      method: 'GET',
      credentials: 'same-origin',
      headers: { // set headers in fetch polyfill to prevent IE11 caching
        'pragma': 'no-cache',
        'cache-control': 'no-cache'
      }
    }).then(checkStatus)
      .then(parseJSON)
      .then((data) => {
        if (data.error) {
          this.handleError(ERROR_TITLE, data.message);
        } else {
          this.setState({ // eslint-disable-line react/no-did-mount-set-state
            fields: data,
            isLoadingFields: false
          });
        }
      })
      .catch((error) => {
        this.handleError(ERROR_TITLE, error);
      });
  }

  getWorkbenchEndpoint(suffix = '') {
    const { props } = this;

    if (this.isAccountLevel()) {
      return `/api/accounts/${ props.accountId }/workbench_fields${ suffix }`;
    }
    return `/api/campaigns/${ props.campaignId }/workbench_fields${ suffix }`;
  }

  isAccountLevel() {
    const { campaignId } = this.props;
    return (campaignId === '');
  }

  assertValidField(field) { // eslint-disable-line complexity
    const {
      campaignIds, isAvailableToAccount, isAvailableToAllCampaigns,
      name, type,
    } = field;

    if (!name || name.length === 0) {
      throw new Error('Please enter a non-empty Attribute Name');
    }
    if (!type) {
      throw new Error('Please select an Attribute Type');
    }

    if (this.isAccountLevel()) {
      if (! isAvailableToAccount) {
        throw new Error('The Attribute must be available to the Account');
      }
      if (isAvailableToAllCampaigns) {
        if (campaignIds.length !== 0) {
          throw new Error('Please do not select individual Workspaces; the field is shared with all Workspaces');
        }
      }
      else if (campaignIds.length === 0) {
        throw new Error('Please select the Workspaces which share this Attribute.');
      }
    }
    else {
      const { campaignId } = this.props;
      if (isAvailableToAccount) {
        throw new Error('The Attribute must only be available to the Workspace');
      }
      if (! ((campaignIds.length === 1) && (campaignIds[0] === campaignId))) {
        throw new Error('The Attribute must only be available to the Workspace');
      }
    }
  }

  assertValidFieldValue(value) {
    if (! isObject(value)) {
      throw new Error('Please enter a non-empty value');
    }

    const { name } = value;

    if (!name || name.length === 0) {
      throw new Error('Please enter a non-empty Value Name');
    }
  }

  handleError(title, message){
    this.props.toastr._errorMessage({
      error_title: title,
      error_message: message
    });
  }

  handleFieldUpdate(field) {
    const { _id: fieldId } = field;

    try {
      this.assertValidField(field);
    }
    catch (e) {
      this.handleError('Field not changed', e.message);
      return Promise.resolve();
    }

    const ERROR_TITLE = 'Server failed to update a field';
    const body = pick(field, [
      // the Builder only computes foreign references for new fields, not updates
      'accountId', 'campaignIds', 'isAvailableToAccount', 'isAvailableToAllCampaigns',
      'name', // editable in-place
      'type', // for server-side logic
      // nothing else is update-able through our UI
    ]);

    return fetch(this.getWorkbenchEndpoint(`/${ fieldId }`), {
      method: 'PUT',
      credentials: 'same-origin',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body),
    }).then(checkStatus)
      .then(parseJSON)
      .then((data) => {
        if (data.error) {
          this.handleError(ERROR_TITLE, data.message);
        } else {
          const fields = this.state.fields.slice();
          const fieldIndex = findIndex(fields, {_id: fieldId});
          if (fieldIndex !== -1) {
            fields[fieldIndex] = field;
          }
          this.setState({fields});
        }
      }).catch((error) => {
        this.handleError(ERROR_TITLE, error);
      });
  }

  handleFieldDelete(fieldId) {
    const ERROR_TITLE = 'Server failed to delete field';

    return fetch(this.getWorkbenchEndpoint(`/${ fieldId }`), {
      method: 'DELETE',
      credentials: 'same-origin',
    }).then(checkStatus)
      .then(parseJSON)
      .then((data) => {
        if (data.error) {
          this.handleError(ERROR_TITLE, data.message);
        } else {
          const fields = this.state.fields.slice();
          const fieldIndex = findIndex(fields, {_id: fieldId});
          if (fieldIndex !== -1) {
            fields.splice(fieldIndex, 1);
          }
          this.setState({fields});
        }
      }).catch((error) => {
        this.handleError(ERROR_TITLE, error);
      });
  }

  handleFieldAdd(newFieldState, resetNewFieldForm) {
    const ERROR_TITLE = 'Server failed to add WorkWise field';
    const { accountId, campaignId } = this.props;
    const { fields } = this.state;
    const { fieldName, fieldType, fieldHasMultipleValues } = newFieldState; // from WorkbenchFieldForm

    // the "Builder knows best" when it comes to computing initial foreign references;
    //   except for the Account-level UI, where the User selects Workspaces
    const isAccountLevel = this.isAccountLevel();
    const campaignIds = (isAccountLevel
      ? newFieldState.fieldCampaignIds
      : [ campaignId ] // only the current Workspace
    );
    const isAvailableToAllCampaigns = (isAccountLevel
      ? newFieldState.fieldIsAvailableToAllCampaigns
      : false // only the current Workspace
    );

    const field = {
      accountId,
      campaignIds,
      isAvailableToAccount: isAccountLevel,
      isAvailableToAllCampaigns,
      name: fieldName,
      type: fieldType,
      multi: (fieldType === 'Categorical' ? fieldHasMultipleValues : false),
      values: [],
    };

    try {
      this.assertValidField(field);
    }
    catch (e) {
      this.handleError('Field not added', e.message);
      return Promise.resolve();
    }

    return fetch(this.getWorkbenchEndpoint(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(field),
    }).then(checkStatus)
      .then(parseJSON)
      .then((data) => {
        if (data.error) {
          this.handleError(ERROR_TITLE, data.message);
        } else {
          data.isNewField = true; // used to automatically open 'add value' for new fields
          this.setState({
            fields: fields.concat(data),
          });
        }
        resetNewFieldForm();
      })
      .catch((error) => {
        this.handleError(ERROR_TITLE, error);
      });
  }

  handleFieldValueUpdate(fieldId, value){
    const ERROR_TITLE = 'Server failed to update WorkWise value';

    try {
      this.assertValidFieldValue(value);
    }
    catch (e) {
      this.handleError('Field value not changed', e.message);
      return Promise.resolve();
    }

    return fetch(this.getWorkbenchEndpoint(`/${ fieldId }/values/${ value._id }`), {
      method: 'PUT',
      credentials: 'same-origin',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        name: value.name,
        workbenchFieldId: fieldId,
      })
    }).then(checkStatus)
      .then(parseJSON)
      .then((data) => {
        if (data.error) {
          this.handleError(ERROR_TITLE, data.message);
        } else {
          const fields = this.state.fields.slice();
          const fieldIndex = findIndex(fields, {_id: fieldId});
          if (fieldIndex !== -1) {
            const field = fields[fieldIndex];
            const values = field.values.slice();
            const valueIndex = findIndex(values, {_id: value._id});
            values[valueIndex] = value;
            fields[fieldIndex] = {
              ...field,
              values,
            };
          }
          this.setState({fields});
        }
      })
      .catch((error) => {
        this.handleError(ERROR_TITLE, error);
      });
  }

  handleFieldValueDelete(fieldId, valueId) {
    const ERROR_TITLE = 'Server failed to delete WorkWise value';

    return fetch(this.getWorkbenchEndpoint(`/${ fieldId }/values/${ valueId }`), {
      method: 'DELETE',
      credentials: 'same-origin',
    }).then(checkStatus)
      .then(parseJSON)
      .then((data) => {
        if (data.error) {
          this.handleError(ERROR_TITLE, data.message);
        } else {
          const fields = this.state.fields.slice();
          const fieldIndex = findIndex(fields, {_id: fieldId});
          if (fieldIndex !== -1) {
            const field = fields[fieldIndex];
            const values = field.values.slice();
            const valueIndex = findIndex(values, {_id: valueId});
            values.splice(valueIndex, 1);
            fields[fieldIndex] = {
              ...field,
              values,
            };
          }
          this.setState({fields});
        }
      })
      .catch((error) => {
        this.handleError(ERROR_TITLE, error);
      });
  }

  handleFieldValueAdd(fieldId, value) {
    const ERROR_TITLE = 'Server failed to add WorkWise value';

    try {
      this.assertValidFieldValue(value);
    }
    catch (e) {
      this.handleError('Field value not added', e.message);
      return Promise.resolve();
    }

    return fetch(this.getWorkbenchEndpoint(`/${ fieldId }/values`), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        name: value.name,
        workbenchFieldId: fieldId,
      })
    }).then(checkStatus)
      .then(parseJSON)
      .then((data) => {
        if (data.error) {
          this.handleError(ERROR_TITLE, data.message);
        } else {
          const fields = this.state.fields.slice();
          const fieldIndex = findIndex(fields, {_id: fieldId});
          if (fieldIndex !== -1) {
            const field = fields[fieldIndex];
            fields[fieldIndex] = {
              ...field,
              values: field.values.concat(data),
            };
          }
          this.setState({fields});
        }
      })
      .catch((error) => {
        this.handleError(ERROR_TITLE, error);
      });
  }

  render() {
    const { fields, isLoadingFields } = this.state;
    const { campaigns } = this.props;
    const isAccountLevel = this.isAccountLevel();
    let titleHeader;
    let descriptiveText = null;
    let fieldBuilder = null;

    if (isAccountLevel) {
      titleHeader = 'Shared Custom Attributes';
      descriptiveText = (<div className={styles.descriptiveText}>
        Create Custom Attributes and share them with workspaces.
        Any changes made to Custom Attributes here will be reflected
        across all workspaces that use those Custom Attributes.
      </div>);
    }
    else {
      titleHeader = 'Custom Attributes';
    }

    if (isLoadingFields === false) {
      fieldBuilder = (<div>
        <div className={styles.titleSection}>
          <h1 className={styles.titleHeader}>{titleHeader}</h1>
          {descriptiveText}
        </div>
        <WorkbenchFieldList
          fields={fields}
          campaigns={campaigns}
          isAccountLevel={isAccountLevel}
          onFieldUpdate={this.handleFieldUpdate}
          onFieldDelete={this.handleFieldDelete}
          onFieldValueUpdate={this.handleFieldValueUpdate}
          onFieldValueAdd={this.handleFieldValueAdd}
          onFieldValueDelete={this.handleFieldValueDelete}
        />
        <WorkbenchFieldForm
          isAccountLevel={isAccountLevel}
          campaigns={campaigns}
          onFieldAdd={this.handleFieldAdd}
        />
      </div>);
    } else {
      fieldBuilder = (<div className={styles.loadingSection}>
        <Loading />
      </div>);
    }

    return (
      <div className={styles.container}>
        {fieldBuilder}
      </div>
    );
  }
}

WorkbenchBuilder.propTypes = {
  fields: PropTypes.array,
  accountId: PropTypes.string.isRequired,
  campaigns: PropTypes.array.isRequired, // Model#attributes from 'campaigns' Bootstrap
  campaignId: PropTypes.string,
  toastr: PropTypes.object.isRequired
};

WorkbenchBuilder.defaultProps = {
  fields: [], // populated with existing fields
  accountId: '', // needed for updating backend
  campaigns: [], // needed for updating backend
  campaignId: '', // needed for updating backend
  toastr: null, // needed for displaying toastr messages to user
};

export default WorkbenchBuilder;
